import OSS from 'ali-oss'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { normalizeAppRuntimeEnv } from '@/config/domainPresets'

type BrowserUtilGlobalStore = typeof globalThis & {
  __AVGMAKER_BROWSER_OSS__?: BrowserUtil
}

type OssConfigInput = Record<string, unknown> | string | null | undefined

const webUrl = new BaseWebUrl()

type OssRuntimeConfig = {
  accessKeyId: string
  accessKeySecret: string
  stsToken: string
  expire?: number
  host?: string
  dir?: string
}

type OssGetPayload = {
  content: unknown
  signedUrl: string
}

let runtimeCredentialPatch: Partial<OssRuntimeConfig> = {}
let runtimeCredentialRefreshPromise: Promise<OssRuntimeConfig> | null = null
let runtimeSessionIdResolver: (() => string) | null = null

/** 注册 OSS badge 请求读取 sessionId 的唯一运行时来源。 */
export function setBrowserOssSessionIdResolver(resolver: (() => string) | null): void {
  runtimeSessionIdResolver = resolver
}

function getOssRuntimeEnv(): string {
  const metaEnv = (import.meta as ImportMeta & {
    env?: {
      MODE?: string
      VITE_APP_ENV?: string
    }
  }).env ?? {}
  const processEnv = (globalThis as typeof globalThis & {
    process?: {
      env?: {
        CG_APP_ENV?: string
        VITE_APP_ENV?: string
      }
    }
  }).process?.env ?? {}
  return normalizeAppRuntimeEnv(
    metaEnv.MODE
    || metaEnv.VITE_APP_ENV
    || processEnv.VITE_APP_ENV
    || processEnv.CG_APP_ENV,
  )
}

function getDefaultOssBucket(): string {
  const env = getOssRuntimeEnv()
  return env.includes('test') ? 'test-ouser' : 'ouser'
}

class SessionExpiredError extends Error {
  constructor(message = '登录已过期，请重新登录后继续') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

function trimSlash(value = ''): string {
  return String(value).replace(/^\/+|\/+$/g, '')
}

function ensureTrailingSlash(value = ''): string {
  const normalized = trimSlash(value)
  return normalized ? `${normalized}/` : ''
}

function joinPath(...parts: string[]): string {
  return parts.map((part) => trimSlash(part)).filter(Boolean).join('/')
}

function getFileName(path = ''): string {
  const normalized = trimSlash(path)
  if (!normalized) return ''
  const segments = normalized.split('/')
  return segments[segments.length - 1] || ''
}

function toPercent(value: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0
  return Math.max(0, Math.min(100, Math.round(value * 100)))
}

function buildDisposition(fileName: string): string {
  const encoded = encodeURIComponent(fileName)
  return `attachment; filename*=UTF-8''${encoded}`
}

function getStringField(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function getStringAlias(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = getStringField(record[key])
    if (value) return value
  }
  return ''
}

function getSignedObjectUrl(client: InstanceType<typeof OSS>, key: string): string {
  const signer = client as InstanceType<typeof OSS> & {
    signatureUrl?: (objectKey: string, options?: { expires?: number }) => string
  }
  return typeof signer.signatureUrl === 'function' ? normalizeOssUrlScheme(signer.signatureUrl(key, { expires: 3600 })) : ''
}

async function fetchSignedObjectBlob(url: string, objectKey: string): Promise<Blob> {
  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
  })
  if (!response.ok) {
    throw new Error(`OSS 直链读取失败：${objectKey} · HTTP ${response.status}`)
  }
  const bytes = await BrowserFileUtils.responseToUint8Array(response)
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
  return new Blob([buffer], {
    type: response.headers.get('content-type') || 'application/octet-stream',
  })
}

function normalizeOssUrlScheme(url: string): string {
  return String(url || '').replace(/^http:\/\//i, 'https://')
}

function getNumberAlias(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = Number(record[key])
    if (Number.isFinite(value)) return value
  }
  return undefined
}

function hasOssClientOptions(options: Record<string, unknown>): boolean {
  return Boolean(
    getStringField(options.region)
    && getStringField(options.bucket)
    && getStringField(options.accessKeyId)
    && getStringField(options.accessKeySecret),
  )
}

/**
 * badge 地址优先级：
 * 1. 运行时显式覆盖
 * 2. 开发环境走 Vite 同源代理，避免浏览器 CORS
 * 3. 生产环境直连远端地址
 */
function getRuntimeBadgeUrl(): string {
  if (typeof window !== 'undefined') {
    const override = getStringField(
      (window as Window & {
        __AVGMAKER_OSS_BADGE_URL__?: string
      }).__AVGMAKER_OSS_BADGE_URL__,
    )
    if (override) return override
  }

  return webUrl.ossBadgeUrl
}

function getRuntimeSessionId(): string {
  const runtimeSessionId = getStringField(runtimeSessionIdResolver?.())
  if (runtimeSessionId) return runtimeSessionId
  return ''
}

function isSessionExpiredPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false
  const record = payload as Record<string, unknown>
  const ret = Number(record.ret ?? NaN)
  if (Number.isFinite(ret) && ret !== 1) return true
  const message = getPayloadMessage(payload).toLowerCase()
  return ['session', 'login', '登录', '会话', '过期', '失效'].some((keyword) => message.includes(keyword))
}

function appendSessionIdToUrl(url: string, sessionId: string): string {
  if (!sessionId) return url
  const parsed = new URL(url, window.location.origin)
  parsed.searchParams.set('sessionid', sessionId)
  return parsed.pathname.startsWith('/') && parsed.origin === window.location.origin
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.toString()
}

function getPayloadMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return ''
  const record = payload as Record<string, unknown>
  return getStringField(record.msg)
    || getStringField(record.message)
    || getStringField(record.error)
    || getStringField(record.code)
}

function normalizeRuntimeConfig(value: unknown): OssRuntimeConfig | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const accessKeyId = getStringAlias(record, ['accessKeyId', 'AccessKeyId', 'access_key_id', 'accessid'])
  const accessKeySecret = getStringAlias(record, [
    'accessKeySecret',
    'AccessKeySecret',
    'access_key_secret',
    'accesskey',
  ])
  const stsToken = getStringAlias(record, ['stsToken', 'StsToken', 'SecurityToken', 'securityToken', 'token'])
  if (!accessKeyId || !accessKeySecret || !stsToken) return null
  const expire = getNumberAlias(record, ['expire', 'expires', 'expireTime'])
  return {
    accessKeyId,
    accessKeySecret,
    stsToken,
    expire,
    host: getStringAlias(record, ['host', 'Host']),
    dir: getStringAlias(record, ['dir', 'Dir', 'uploadDir']),
  }
}

function findRuntimeConfig(value: unknown): OssRuntimeConfig | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  return normalizeRuntimeConfig(record.data)
    ?? normalizeRuntimeConfig((record.data as Record<string, unknown> | undefined)?.credentials)
    ?? normalizeRuntimeConfig((record.data as Record<string, unknown> | undefined)?.Credentials)
    ?? normalizeRuntimeConfig(record.result)
    ?? normalizeRuntimeConfig((record.result as Record<string, unknown> | undefined)?.data)
    ?? normalizeRuntimeConfig(record.credentials)
    ?? normalizeRuntimeConfig(record.Credentials)
    ?? normalizeRuntimeConfig(record)
}

async function fetchRuntimeCredentialPatchOnce(sessionId: string): Promise<OssRuntimeConfig> {
  const badgeUrl = appendSessionIdToUrl(getRuntimeBadgeUrl(), sessionId)
  const response = await fetch(badgeUrl, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  })
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new SessionExpiredError()
    }
    throw new Error(`刷新 OSS 凭证失败：HTTP ${response.status}`)
  }

  const payload = (await response.json()) as unknown
  if (isSessionExpiredPayload(payload)) {
    throw new SessionExpiredError(getPayloadMessage(payload) || undefined)
  }
  const patch = findRuntimeConfig(payload)
  if (!patch) {
    throw new Error('刷新 OSS 凭证失败：响应中缺少 accessKeyId/accessKeySecret/stsToken')
  }

  runtimeCredentialPatch = patch
  return patch
}

async function fetchRuntimeCredentialPatch(): Promise<OssRuntimeConfig> {
  if (runtimeCredentialRefreshPromise) {
    return await runtimeCredentialRefreshPromise
  }

  runtimeCredentialRefreshPromise = (async () => {
    const sessionId = getRuntimeSessionId()
    if (!sessionId) {
      throw new SessionExpiredError('当前没有有效登录态，请重新登录后继续')
    }
    try {
      return await fetchRuntimeCredentialPatchOnce(sessionId)
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) throw error
      throw error
    }
  })()

  try {
    return await runtimeCredentialRefreshPromise
  } finally {
    runtimeCredentialRefreshPromise = null
  }
}

function isOssCredentialExpired(error: unknown): boolean {
  const record = error && typeof error === 'object' ? (error as Record<string, unknown>) : {}
  const message = String(
    record.message ?? record.code ?? record.name ?? record.errorMessage ?? record.statusMessage ?? '',
  ).toLowerCase()
  const status = Number(record.status ?? record.statusCode ?? record.httpStatusCode ?? 0)

  if (status === 401 || status === 403) return true

  return [
    'securitytokenexpired',
    'tokenexpired',
    'sts token',
    'ststoken',
    'invalidaccesskeyid',
    'accesskeysecret',
    'signaturedoesnotmatch',
    'forbidden',
    'http 403',
    'expiredtoken',
  ].some((keyword) => message.includes(keyword))
}

function readEnvConfig(): Record<string, unknown> {
  return {
    region: 'oss-cn-hangzhou',
    bucket: getDefaultOssBucket(),
    secure: true,
    ...runtimeCredentialPatch,
  }
}

function readWindowConfig(): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  const raw = (window as Window & {
    __AVGMAKER_OSS_CONFIG__?: Record<string, unknown>
    __AVGMAKER_OSS_OPTIONS__?: Record<string, unknown>
  }).__AVGMAKER_OSS_CONFIG__ ?? (window as Window & {
    __AVGMAKER_OSS_OPTIONS__?: Record<string, unknown>
  }).__AVGMAKER_OSS_OPTIONS__
  if (!raw || typeof raw !== 'object') return {}
  return { ...raw }
}

function parseConfig(config: OssConfigInput): {
  clientOptions: Record<string, unknown>
  rootDir: string
} {
  let raw = config ?? {}
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw) as Record<string, unknown>
    } catch {
      raw = {}
    }
  }
  if (!raw || typeof raw !== 'object') {
    raw = {}
  }

  const normalized = { ...raw } as Record<string, unknown>
  const rootDir = typeof normalized.dir === 'string' ? normalized.dir.trim() : ''

  if (typeof normalized.host === 'string' && normalized.host.trim()) {
    try {
      const url = new URL(normalized.host)
      const segments = url.hostname.split('.').filter(Boolean)
      if (!normalized.bucket) normalized.bucket = segments[0] || ''
      if (!normalized.region) normalized.region = segments[1] || ''
      if (!normalized.endpoint) normalized.endpoint = `https://${segments.slice(1).join('.')}`
    } catch {
      /* ignore invalid host */
    }
  }

  delete normalized.host
  delete normalized.dir
  delete normalized.expire
  if (typeof normalized.endpoint === 'string') {
    normalized.endpoint = normalized.endpoint.replace(/^http:\/\//i, 'https://')
  }
  normalized.secure = true

  return {
    rootDir,
    clientOptions: normalized,
  }
}

export type BrowserOssListFolderItem = {
  key: string
  name: string
  relativePath: string
}

export type BrowserOssWriteDataInput = Blob | ArrayBuffer | Uint8Array | string

export default class BrowserUtil {
  private static instance: BrowserUtil | null = null

  private client: InstanceType<typeof OSS> | null = null

  private clientOptions: Record<string, unknown> = {}

  private rootDir = ''

  private credentialRetryPromise: Promise<void> | null = null

  constructor(config?: OssConfigInput) {
    if (config) {
      this.init(config)
    }
  }

  static getInstance(): BrowserUtil {
    const store = globalThis as BrowserUtilGlobalStore
    if (!store.__AVGMAKER_BROWSER_OSS__) {
      store.__AVGMAKER_BROWSER_OSS__ = new BrowserUtil()
    }
    BrowserUtil.instance = store.__AVGMAKER_BROWSER_OSS__
    return BrowserUtil.instance
  }

  static getDefaultConfig(): Record<string, unknown> {
    return {
      ...readEnvConfig(),
      ...readWindowConfig(),
    }
  }

  private applyClientOptions(options: Record<string, unknown>): void {
    this.clientOptions = { ...options }
    if (hasOssClientOptions(options)) {
      this.client = new OSS(options as ConstructorParameters<typeof OSS>[0])
      return
    }
    this.client = null
  }

  init(config?: OssConfigInput): this {
    const parsed = parseConfig(config)
    this.applyClientOptions(parsed.clientOptions)
    if (parsed.rootDir) {
      this.setRootDir(parsed.rootDir)
    }
    return this
  }

  initDefaultConfig(): this {
    return this.init(BrowserUtil.getDefaultConfig())
  }

  connect(uid: number | string): this {
    const config = BrowserUtil.getDefaultConfig()
    const parsed = parseConfig(config)
    this.applyClientOptions(parsed.clientOptions)
    this.setRootDir(parsed.rootDir || String(uid || '').trim())
    return this
  }

  clear(): this {
    runtimeCredentialPatch = {}
    runtimeCredentialRefreshPromise = null
    this.client = null
    this.clientOptions = {}
    this.setRootDir('')
    return this
  }

  setConfig(config?: OssConfigInput): this {
    return this.init(config)
  }

  setClientOptions(config?: OssConfigInput): this {
    return this.init(config)
  }

  isConfigured(): boolean {
    return !!this.client || !!getRuntimeSessionId()
  }

  setRootDir(rootDir = ''): string {
    this.rootDir = ensureTrailingSlash(rootDir)
    return this.rootDir
  }

  getRootDir(): string {
    return this.rootDir
  }

  resolveObjectKey(path = ''): string {
    const normalized = trimSlash(path)
    return normalized ? joinPath(this.rootDir, normalized) : trimSlash(this.rootDir)
  }

  getObjectUrl(objectKey: string): string {
    const key = this.resolveObjectKey(objectKey)
    const bucket = String(this.clientOptions.bucket ?? '').trim()
    const endpoint = String(this.clientOptions.endpoint ?? '').trim().replace(/\/+$/g, '')
    const region = String(this.clientOptions.region ?? '').trim()
    const secure = this.clientOptions.secure !== false
    if (!key || !bucket) return ''
    if (endpoint) {
      return `${endpoint}/${key}`
    }
    if (region) {
      return `${secure ? 'https' : 'http'}://${bucket}.${region}.aliyuncs.com/${key}`
    }
    return ''
  }

  private getClient(): InstanceType<typeof OSS> {
    if (!this.client) {
      throw new Error('OSS 未初始化，请先调用 init(config)')
    }
    return this.client
  }

  private async refreshCredentials(): Promise<void> {
    if (this.credentialRetryPromise) {
      await this.credentialRetryPromise
      return
    }

    this.credentialRetryPromise = (async () => {
      const patch = await fetchRuntimeCredentialPatch()
      const parsed = parseConfig({
        ...this.clientOptions,
        ...patch,
      })
      this.applyClientOptions(parsed.clientOptions)
      if (parsed.rootDir) {
        this.setRootDir(parsed.rootDir)
      }
    })()

    try {
      await this.credentialRetryPromise
    } finally {
      this.credentialRetryPromise = null
    }
  }

  private async withCredentialRetry<T>(action: (client: InstanceType<typeof OSS>) => Promise<T>): Promise<T> {
    try {
      if (!this.client) {
        await this.refreshCredentials()
      }
      return await action(this.getClient())
    } catch (error) {
      if (!isOssCredentialExpired(error)) {
        throw error
      }
      try {
        await this.refreshCredentials()
      } catch (error) {
        throw error
      }
      return await action(this.getClient())
    }
  }

  private async listFoldersWithClient(
    client: InstanceType<typeof OSS>,
    folderPath = '',
  ): Promise<BrowserOssListFolderItem[]> {
    const prefix = ensureTrailingSlash(this.resolveObjectKey(folderPath))
    let marker = ''
    const names: BrowserOssListFolderItem[] = []

    do {
      const result = await client.list(
        {
          prefix,
          delimiter: '/',
          marker,
          'max-keys': 1000,
        },
        {},
      )

      const prefixes = Array.isArray(result.prefixes) ? result.prefixes : []
      for (const item of prefixes) {
        const key = String(item || '')
        const relativePath = prefix ? key.slice(prefix.length) : key
        const folder = trimSlash(relativePath)
        if (!folder) continue
        names.push({
          key,
          name: getFileName(folder),
          relativePath: folder,
        })
      }

      marker = result.isTruncated ? String(result.nextMarker || '') : ''
    } while (marker)

    return names
  }

  private async listObjectsWithClient(
    client: InstanceType<typeof OSS>,
    folderPath = '',
  ): Promise<Array<{ key: string; name: string; relativePath: string }>> {
    const prefix = ensureTrailingSlash(this.resolveObjectKey(folderPath))
    let marker = ''
    const objects: Array<{ key: string; name: string; relativePath: string }> = []
    do {
      const result = await client.list(
        {
          prefix,
          marker,
          'max-keys': 1000,
        },
        {},
      )
      const items = Array.isArray(result.objects) ? result.objects : []
      for (const item of items) {
        const key = String(item?.name || '')
        if (!key) continue
        const relativePath = prefix ? key.slice(prefix.length) : key
        objects.push({
          key,
          name: getFileName(key),
          relativePath: trimSlash(relativePath),
        })
      }
      marker = result.isTruncated ? String(result.nextMarker || '') : ''
    } while (marker)
    return objects
  }

  async createFolder(folderPath: string): Promise<{
    key: string
    name: string
    url?: string
    res?: unknown
  }> {
    return await this.withCredentialRetry(async (client) => {
      const folderKey = ensureTrailingSlash(this.resolveObjectKey(folderPath))
      if (!folderKey) throw new Error('folderPath 不能为空')
      const result = await client.put(folderKey, new Blob([]))
      return {
        key: folderKey,
        name: getFileName(folderKey),
        url: result.url,
        res: result.res,
      }
    })
  }

  async deleteFile(objectKey: string): Promise<void> {
    await this.withCredentialRetry(async (client) => {
      const key = this.resolveObjectKey(objectKey)
      if (!key) throw new Error('objectKey 不能为空')
      await client.delete(key)
    })
  }

  /**
   * 在 OSS 内部复制单个对象，不经过浏览器下载再上传。
   * @param sourceObjectKey 源对象相对路径
   * @param targetObjectKey 目标对象相对路径
   */
  async copyObject(sourceObjectKey: string, targetObjectKey: string): Promise<void> {
    await this.withCredentialRetry(async (client) => {
      const sourceKey = this.resolveObjectKey(sourceObjectKey)
      const targetKey = this.resolveObjectKey(targetObjectKey)
      if (!sourceKey) throw new Error('sourceObjectKey 不能为空')
      if (!targetKey) throw new Error('targetObjectKey 不能为空')
      const copier = client as InstanceType<typeof OSS> & {
        copy?: (name: string, sourceName: string) => Promise<unknown>
      }
      if (typeof copier.copy !== 'function') {
        throw new Error('当前 OSS 客户端不支持对象复制')
      }
      await copier.copy(targetKey, sourceKey)
    })
  }

  async listFolders(folderPath = ''): Promise<BrowserOssListFolderItem[]> {
    return await this.withCredentialRetry((client) => this.listFoldersWithClient(client, folderPath))
  }

  async listObjects(folderPath = ''): Promise<Array<{ key: string; name: string; relativePath: string }>> {
    return await this.withCredentialRetry((client) => this.listObjectsWithClient(client, folderPath))
  }

  async hasObject(objectKey: string): Promise<boolean> {
    return await this.withCredentialRetry(async (client) => {
      const key = this.resolveObjectKey(objectKey)
      if (!key) return false
      const result = await client.list(
        {
          prefix: key,
          'max-keys': 1,
        },
        {},
      )
      const items = Array.isArray(result.objects) ? result.objects : []
      return items.some((item: { name?: unknown }) => String(item?.name || '') === key)
    })
  }

  async deleteFolder(folderPath: string): Promise<{ prefix: string; deletedCount: number }> {
    return await this.withCredentialRetry(async (client) => {
      const prefix = ensureTrailingSlash(this.resolveObjectKey(folderPath))
      if (!prefix) throw new Error('folderPath 不能为空')
      const objects = await this.listObjectsWithClient(client, folderPath)
      if (!objects.length) {
        return { prefix, deletedCount: 0 }
      }
      const keys = objects.map((item) => item.key)
      for (let index = 0; index < keys.length; index += 1000) {
        const chunk = keys.slice(index, index + 1000)
        if (!chunk.length) continue
        await client.deleteMulti(chunk, { quiet: true })
      }
      return {
        prefix,
        deletedCount: keys.length,
      }
    })
  }

  async getText(objectKey: string): Promise<string> {
    return await BrowserFileUtils.blobToText(await this.getBlob(objectKey))
  }

  async getBlob(objectKey: string): Promise<Blob> {
    let payload: OssGetPayload
    try {
      payload = await this.withCredentialRetry(async (client) => {
        const key = this.resolveObjectKey(objectKey)
        const signedUrl = getSignedObjectUrl(client, key)
        try {
          const result = await client.get(key)
          return {
            content: result.content,
            signedUrl,
          }
        } catch (error) {
          if (isOssCredentialExpired(error) || !signedUrl) {
            throw error
          }
          return {
            content: null,
            signedUrl,
          }
        }
      })
    } catch (error) {
      throw new Error(`OSS 文件读取失败：${objectKey}`, { cause: error })
    }

    const content = payload.content
    if (content instanceof Blob) return content
    if (content instanceof ArrayBuffer) return new Blob([content])
    if (content instanceof Uint8Array) {
      const buffer = content.buffer.slice(
        content.byteOffset,
        content.byteOffset + content.byteLength,
      ) as ArrayBuffer
      return new Blob([buffer])
    }
    if (typeof content === 'string') return new Blob([content], { type: 'text/plain;charset=utf-8' })
    if (content == null && payload.signedUrl) return await fetchSignedObjectBlob(payload.signedUrl, objectKey)
    if (content == null) throw new Error(`OSS 文件读取失败：${objectKey} 返回内容为空`)
    return new Blob([String(content)], { type: 'text/plain;charset=utf-8' })
  }

  async getUint8Array(objectKey: string): Promise<Uint8Array> {
    const blob = await this.getBlob(objectKey)
    return new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(blob))
  }

  /**
   * 以“对象路径 + 数据”的方式直接写入 OSS。
   * `objectKey` 支持带目录的相对路径，例如 `save/1.png`。
   */
  async writeData(
    objectKey: string,
    data: BrowserOssWriteDataInput,
    options: {
      contentType?: string
      headers?: Record<string, string>
      meta?: Record<string, string>
      onProgress?: (payload: {
        type: 'upload'
        key: string
        fileName: string
        percent: number
        loaded: number
        total: number
        checkpoint?: unknown
        res?: unknown
      }) => void
      parallel?: number
      partSize?: number
      useMultipart?: boolean
    } = {},
  ): Promise<{
    key: string
    name: string
    url?: string
    etag?: string
    requestId?: string
    raw: unknown
  }> {
    const normalizedKey = trimSlash(objectKey)
    if (!normalizedKey) {
      throw new Error('objectKey 不能为空')
    }

    let blob: Blob
    if (data instanceof Blob) {
      blob = data
    } else if (typeof data === 'string') {
      blob = new Blob([data], { type: options.contentType || 'text/plain;charset=utf-8' })
    } else if (data instanceof Uint8Array) {
      const buffer = data.buffer.slice(
        data.byteOffset,
        data.byteOffset + data.byteLength,
      ) as ArrayBuffer
      blob = new Blob([buffer], { type: options.contentType || 'application/octet-stream' })
    } else {
      blob = new Blob([data], { type: options.contentType || 'application/octet-stream' })
    }

    const fileName = getFileName(normalizedKey) || 'file'
    const headers = {
      ...(blob.type ? { 'Content-Type': blob.type } : {}),
      ...(options.headers || {}),
    }

    return await this.uploadFile(blob, {
      objectKey: normalizedKey,
      fileName,
      headers,
      meta: options.meta,
      onProgress: options.onProgress,
      parallel: options.parallel,
      partSize: options.partSize,
      useMultipart: options.useMultipart,
    })
  }

  /**
   * 以“对象路径”直接读取 OSS 中的原始二进制数据。
   * `objectKey` 支持带目录的相对路径，例如 `save/1.png`。
   */
  async readData(objectKey: string): Promise<Uint8Array> {
    const normalizedKey = trimSlash(objectKey)
    if (!normalizedKey) {
      throw new Error('objectKey 不能为空')
    }
    return await this.getUint8Array(normalizedKey)
  }

  async uploadFile(
    file: Blob & { name?: string },
    options: {
      objectKey?: string
      fileName?: string
      onProgress?: (payload: {
        type: 'upload'
        key: string
        fileName: string
        percent: number
        loaded: number
        total: number
        checkpoint?: unknown
        res?: unknown
      }) => void
      headers?: Record<string, string>
      meta?: Record<string, string>
      parallel?: number
      partSize?: number
      useMultipart?: boolean
    } = {},
  ): Promise<{
    key: string
    name: string
    url?: string
    etag?: string
    requestId?: string
    raw: unknown
  }> {
    return await this.withCredentialRetry(async (client) => {
      const fileName = options.fileName || file.name || 'file'
      const key = this.resolveObjectKey(options.objectKey || fileName)
      const total = file.size || 0

      options.onProgress?.({
        type: 'upload',
        key,
        fileName,
        percent: 0,
        loaded: 0,
        total,
      })

      if (!options.useMultipart) {
        const result = await client.put(key, file, {
          headers: options.headers,
          meta: options.meta,
        })
        options.onProgress?.({
          type: 'upload',
          key,
          fileName,
          percent: 100,
          loaded: total,
          total,
          res: result.res,
        })
        return {
          key,
          name: fileName,
          url: result.url,
          etag: result.res?.headers?.etag,
          requestId: result.res?.headers?.['x-oss-request-id'],
          raw: result,
        }
      }

      const result = await client.multipartUpload(key, file, {
        parallel: options.parallel,
        partSize: options.partSize,
        headers: options.headers,
        meta: options.meta,
        progress: async (percentage: number, checkpoint: unknown, res: unknown) => {
          options.onProgress?.({
            type: 'upload',
            key,
            fileName,
            percent: toPercent(percentage),
            loaded: total > 0 ? Math.round(total * percentage) : 0,
            total,
            checkpoint,
            res,
          })
        },
      })

      options.onProgress?.({
        type: 'upload',
        key,
        fileName,
        percent: 100,
        loaded: total,
        total,
        checkpoint: result.checkpoint,
        res: result.res,
      })

      return {
        key,
        name: fileName,
        url: result.url,
        etag: result.etag,
        requestId: result.res?.headers?.['x-oss-request-id'],
        raw: result,
      }
    })
  }

  async downloadFile(
    objectKey: string,
    options: {
      fileName?: string
      expires?: number
      autoSave?: boolean
      onProgress?: (payload: {
        type: 'download'
        key: string
        fileName: string
        percent: number
        loaded: number
        total: number
      }) => void
    } = {},
  ): Promise<{
    key: string
    name: string
    url: string
    blob: Blob
    size: number
  }> {
    return await this.withCredentialRetry(async (client) => {
      const key = this.resolveObjectKey(objectKey)
      if (!key) throw new Error('objectKey 不能为空')

      const fileName = options.fileName || getFileName(key) || 'download'
      const signedUrl = normalizeOssUrlScheme(client.signatureUrl(key, {
        expires: options.expires ?? 300,
        response: {
          'content-disposition': buildDisposition(fileName),
        },
      }))

      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', signedUrl, true)
        xhr.responseType = 'blob'

        xhr.onprogress = (event) => {
          const total = event.lengthComputable ? event.total : 0
          const percent = total > 0 ? Math.round((event.loaded / total) * 100) : 0
          options.onProgress?.({
            type: 'download',
            key,
            fileName,
            percent,
            loaded: event.loaded,
            total,
          })
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const nextBlob = xhr.response
            options.onProgress?.({
              type: 'download',
              key,
              fileName,
              percent: 100,
              loaded: nextBlob?.size || 0,
              total: nextBlob?.size || 0,
            })
            resolve(nextBlob)
            return
          }
          reject(new Error(`下载失败，HTTP ${xhr.status}`))
        }

        xhr.onerror = () => reject(new Error('下载失败，网络异常'))
        xhr.send()
      })

      if (options.autoSave !== false) {
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        link.remove()
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
      }

      return {
        key,
        name: fileName,
        url: signedUrl,
        blob,
        size: blob.size,
      }
    })
  }
}
